// src/pages/Home.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";

/* assets */
import CoolBlue from "../assets/LightOffwhite.png";
import aboutImg from "../assets/about-4.png";

/* data */
import { getPrayerTimes } from "../services/masjidService";

/* ------------------------ CONFIG ------------------------ */
const MASJID_ID = Number(process.env.REACT_APP_MASJID_ID || 36);

/* ------------------------ small utils ------------------------ */
const getProp = (o, arr, fb) => {
  for (const k of arr) if (o && o[k] != null) return o[k];
  return fb;
};

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const parseDateSafe = (v) => {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

const toDateOnly = (v) => {
  const d = parseDateSafe(v);
  if (!d) return null;
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const formatDateDisplay = (v) => {
  const d = parseDateSafe(v);
  if (!d) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const cx = (...parts) => parts.filter(Boolean).join(" ");

const sameDay = (a, b) => {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
};

const dateKey = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/* normalize "7", "7:5", "07:05 PM", "19:05" -> "h:mm AM/PM" */
function normalizeTimeString(raw) {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^sunset$/i.test(s)) return "Sunset";

  const m12 = s.match(/^(\d{1,2})(?::(\d{1,2}))?\s*([AaPp][Mm])$/);
  if (m12) {
    let h = clamp(parseInt(m12[1], 10) || 12, 1, 12);
    const mm = String(clamp(parseInt(m12[2] ?? "0", 10) || 0, 0, 59)).padStart(2, "0");
    return `${h}:${mm} ${m12[3].toUpperCase()}`;
  }
  const m24 = s.match(/^(\d{1,2})(?::(\d{1,2}))$/);
  if (m24) {
    const hh = clamp(parseInt(m24[1], 10) || 0, 0, 23);
    const mm = clamp(parseInt(m24[2], 10) || 0, 0, 59);
    const mer = hh >= 12 ? "PM" : "AM";
    const h12 = (hh % 12) || 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${mer}`;
  }
  const mh = s.match(/^(\d{1,2})$/);
  if (mh) {
    const hh = parseInt(mh[1], 10);
    if (hh >= 0 && hh <= 23) {
      const mer = hh >= 12 ? "PM" : "AM";
      const h12 = (hh % 12) || 12;
      return `${h12}:00 ${mer}`;
    }
  }
  return s;
}

/* --- Jumu'ah extractor: returns [{ name, start, iqamah }, ...] --- */
function getJummahsFromData(data) {
  const HARD_FALLBACK = [
    { name: "JUMMAH 1", start: "1:30 PM", iqamah: "1:45 PM" },
    { name: "JUMMAH 2", start: "2:15 PM", iqamah: "2:30 PM" },
  ];

  if (!data?.jummahTimes && !data?.jumuahTimes) return HARD_FALLBACK;

  const norm = (v) => normalizeTimeString(v && String(v).trim());
  const getFuzzy = (obj, re) => {
    for (const k of Object.keys(obj || {})) if (re.test(k)) return obj[k];
    return undefined;
  };

  const arr = data.jummahTimes || data.jumuahTimes;
  if (Array.isArray(arr) && arr.length) {
    const out = [...arr]
      .sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
      .map((j, idx) => {
        const startRaw =
          j.start ??
          j.startTime ??
          j.lecture_Time ??
          j.khutba_Time ??
          j.khutbah_Time ??
          j.khutba ??
          j.khutba_time ??
          j.khutbah_time ??
          j.lecture_time ??
          getFuzzy(j, /(start|jummah|khutb|lecture)/i);

        const iqamahRaw =
          j.iqama_Time ??
          j.iqamah_Time ??
          j.iqma_Time ??
          j.salah_Time ??
          j.prayer_Time ??
          j.iqama_time ??
          j.iqamah_time ??
          j.salah_time ??
          j.prayer_time ??
          getFuzzy(j, /(iqam|salah|prayer)/i);

        return {
          name: `JUMMAH ${idx + 1}`,
          start: norm(startRaw) || "",
          iqamah: norm(iqamahRaw) || "",
          lecture_Time: norm(j.lecture_Time || j.lecture_time || "") || "",
        };
      });

    for (let i = 0; i < Math.max(out.length, HARD_FALLBACK.length); i++) {
      if (!out[i]) out[i] = { ...HARD_FALLBACK[i] };
      if (!out[i].start) out[i].start = HARD_FALLBACK[i]?.start;
      if (!out[i].iqamah) out[i].iqamah = HARD_FALLBACK[i]?.iqamah;
    }
    return out;
  }

  const adhan = data?.startTimes?.timings || data?.timings || data?.data?.timings || {};
  const iq = data?.iqamatimes || data?.iqamahTimes || {};
  const RE_HEAD = /^(?:ju(?:mm?a?h|mu'?a?h)|khutba?h)\s*(\d+)?/i;

  const found = new Map(); // n -> { start?, iqamah? }
  const upd = (n, part) => found.set(n, { ...(found.get(n) || {}), ...part });

  Object.keys(adhan).forEach((k) => {
    const v = adhan[k];
    const m = k.match(RE_HEAD);
    if (m) {
      const n = parseInt(m[1] || "1", 10);
      upd(n, { start: norm(v) });
    } else if (/lecture|khutb|start/i.test(k)) {
      const n = parseInt((k.match(/(\d+)/) || [, "1"])[1], 10);
      upd(n, { start: norm(v) });
    }
  });

  Object.keys(iq).forEach((k) => {
    const v = iq[k];
    if (/(?:iqama|iqamah|salah|prayer)\s*\d*/i.test(k) || RE_HEAD.test(k)) {
      const n = parseInt((k.match(/(\d+)/) || [, "1"])[1], 10);
      upd(n, { iqamah: norm(v) });
    }
  });

  let out = Array.from(found.keys())
    .sort((a, b) => a - b)
    .map((n) => ({
      name: `JUMMAH ${n}`,
      start: found.get(n)?.start || "",
      iqamah: found.get(n)?.iqamah || "",
    }));

  if (!out.length) out = HARD_FALLBACK;
  for (let i = 0; i < Math.max(out.length, HARD_FALLBACK.length); i++) {
    if (!out[i]) out[i] = { ...HARD_FALLBACK[i] };
    if (!out[i].start) out[i].start = HARD_FALLBACK[i]?.start;
    if (!out[i].iqamah) out[i].iqamah = HARD_FALLBACK[i]?.iqamah;
  }

  return out;
}

/* ---- Prayer timings formatter (5 prayers + Sunrise; no Jumu'ah here) ---- */
function formatPrayerTimesForDisplay(prayerData, opts = {}) {
  const { maghribOffsetMinutes = 0, fallbackToStartsForIqamah = true, emptyLabel = "—" } = opts;

  const iqamah = prayerData?.iqamatimes || prayerData?.iqamahTimes || {};
  const adhan = prayerData?.startTimes?.timings || prayerData?.timings || prayerData?.data?.timings || {};

  const safe = (v) => (v ? normalizeTimeString(v) : null);
  const addMinutes = (timeStr, minutesToAdd = 0) => {
    const s = safe(timeStr);
    if (!s) return null;
    const m = s.match(/^(\d{1,2}):(\d{2})\s*([AP]M)$/);
    if (!m) return s;
    const h = parseInt(m[1], 10) % 12;
    const mins = parseInt(m[2], 10);
    const pm = m[3] === "PM";
    let total = (pm ? h + 12 : h) * 60 + mins + minutesToAdd;
    total = ((total % (24 * 60)) + (24 * 60)) % (24 * 60);
    const hh = Math.floor(total / 60);
    const mm = total % 60;
    const mer = hh >= 12 ? "PM" : "AM";
    const h12 = (hh % 12) || 12;
    return `${h12}:${String(mm).padStart(2, "0")} ${mer}`;
  };
  const iqOrStart = (iq, start) => iq || (fallbackToStartsForIqamah ? start || emptyLabel : emptyLabel);

  const fajrStart = safe(getProp(adhan, ["Fajr", "fajr"]));
  const sunriseStart = safe(getProp(adhan, ["Sunrise", "sunrise"]));
  const dhuhrStart = safe(getProp(adhan, ["Dhuhr", "dhuhr", "Zuhr", "zuhr"]));
  const asrStart = safe(getProp(adhan, ["Asr", "asr"]));
  const maghribStart = safe(getProp(adhan, ["Maghrib", "maghrib", "Sunset", "sunset"])) || "Sunset";
  const ishaStart = safe(getProp(adhan, ["Isha", "isha"]));

  const fajrIq = safe(getProp(iqamah, ["fajr", "Fajr", "fajr_iqama", "Fajr_Iqama"]));
  const dhuhrIq = safe(getProp(iqamah, ["dhuhr", "Dhuhr", "zuhr", "Zuhr", "dhuhr_iqama", "Zuhr_Iqama"]));
  const asrIq = safe(getProp(iqamah, ["asr", "Asr", "asr_iqama"]));
  const maghribIqRaw = safe(getProp(iqamah, ["maghrib", "Maghrib", "maghrib_iqama"]));
  const maghribIq =
    maghribIqRaw ||
    (maghribStart
      ? maghribOffsetMinutes
        ? addMinutes(maghribStart, maghribOffsetMinutes)
        : maghribStart
      : emptyLabel);
  const ishaIq = safe(getProp(iqamah, ["isha", "Isha", "isha_iqama"]));

  return {
    prayers: [
      { name: "Fajr", starts: fajrStart || emptyLabel, iqama: iqOrStart(fajrIq, fajrStart) },
      { name: "Sunrise", starts: sunriseStart || emptyLabel, iqama: "" },
      { name: "Dhuhr", starts: dhuhrStart || emptyLabel, iqama: iqOrStart(dhuhrIq, dhuhrStart) },
      { name: "Asr", starts: asrStart || emptyLabel, iqama: iqOrStart(asrIq, asrStart) },
      { name: "Maghrib", starts: maghribStart || "Sunset", iqama: maghribIq || "Sunset" },
      { name: "Isha", starts: ishaStart || emptyLabel, iqama: iqOrStart(ishaIq, ishaStart) },
    ],
  };
}

/* ---------------- Recurrence expansion + occurrence generation ---------------- */

const computeInstanceEndDate = (event, instanceStart) => {
  const origStart = parseDateSafe(event?.startDate ?? event?.start_date ?? event?.date ?? event?.start);
  const origEnd = parseDateSafe(event?.endDate ?? event?.end_date);
  const instStart = parseDateSafe(instanceStart);
  if (!instStart) return instanceStart;

  if (origStart && origEnd && origEnd.getTime() >= origStart.getTime()) {
    const durMs = origEnd.getTime() - origStart.getTime();
    return new Date(instStart.getTime() + durMs);
  }
  return instStart;
};

const expandEventsWithInstances = (events = [], opts = {}) => {
  const { includeParentRuleCarrier = false } = opts;
  const out = [];

  for (const ev of events || []) {
    const isRec = Number(ev?.isRecursive) === 1 || ev?.isRecursive === true;
    const instances = Array.isArray(ev?.instances) ? ev.instances : [];

    if (isRec && instances.length) {
      // instances
      for (const inst of instances) {
        const instDate = inst?.instanceDate ?? inst?.date ?? inst?.startDate;
        const instStart = parseDateSafe(instDate);
        if (!instStart) continue;

        const instEnd = computeInstanceEndDate(ev, instStart);

        out.push({
          ...ev,
          startDate: instStart,
          endDate: instEnd,
          __isInstance: true,
          __instanceDate: instStart,
          __parentEventId: ev?.id ?? ev?.eventId ?? ev?._id ?? null,
        });
      }

      // keep rule carrier
      if (includeParentRuleCarrier) {
        out.push({
          ...ev,
          __isInstance: false,
          __ruleCarrier: true,
          __instanceDate: null,
          __parentEventId: ev?.id ?? ev?.eventId ?? ev?._id ?? null,
        });
      }
    } else {
      out.push({
        ...ev,
        __isInstance: false,
        __ruleCarrier: false,
        __instanceDate: null,
        __parentEventId: null,
      });
    }
  }

  out.sort((a, b) => {
    const aD = parseDateSafe(a?.startDate ?? a?.start_date ?? a?.date ?? a?.start) ?? new Date(0);
    const bD = parseDateSafe(b?.startDate ?? b?.start_date ?? b?.date ?? b?.start) ?? new Date(0);
    return aD.getTime() - bD.getTime();
  });

  return out;
};

const normalizeWeekday = (x) => {
  if (x == null) return null;
  if (typeof x === "number") return x >= 0 && x <= 6 ? x : null;
  const s = String(x).trim().toUpperCase();
  const map = {
    SU: 0, SUN: 0, SUNDAY: 0,
    MO: 1, MON: 1, MONDAY: 1,
    TU: 2, TUE: 2, TUESDAY: 2,
    WE: 3, WED: 3, WEDNESDAY: 3,
    TH: 4, THU: 4, THURSDAY: 4,
    FR: 5, FRI: 5, FRIDAY: 5,
    SA: 6, SAT: 6, SATURDAY: 6,
  };
  return map[s] ?? null;
};

const extractWeekdays = (ev) => {
  const raw =
    ev?.repeatWeekDays ??
    ev?.repeatWeekdays ??
    ev?.weekDays ??
    ev?.daysOfWeek ??
    ev?.repeatOn ??
    ev?.byDay ??
    null;

  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string" && raw.trim()) {
    return raw
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => {
        const asNumber = Number(part);
        return Number.isNaN(asNumber) ? part : asNumber;
      });
  }
  return null;
};

const parseRRule = (rrule) => {
  if (!rrule) return null;
  const s = String(rrule).trim();
  if (!s) return null;

  const parts = s.replace(/^RRULE:/i, "").split(";").map((p) => p.trim()).filter(Boolean);
  const obj = {};
  for (const p of parts) {
    const [k, v] = p.split("=");
    if (!k || v == null) continue;
    obj[k.toUpperCase()] = v;
  }

  const freq = (obj.FREQ || "").toUpperCase();
  const byday = obj.BYDAY ? obj.BYDAY.split(",").map((d) => d.trim()).filter(Boolean) : [];
  const bymonthday = obj.BYMONTHDAY ? obj.BYMONTHDAY.split(",").map((n) => Number(n)).filter((n) => !isNaN(n)) : [];
  const untilRaw = obj.UNTIL || "";
  const until =
    untilRaw
      ? parseDateSafe(untilRaw.includes("T") ? untilRaw.replace("Z", "") : `${untilRaw}T00:00:00`)
      : null;

  return { freq, byday, bymonthday, until };
};

const getEventStartDate = (ev) => parseDateSafe(ev?.startDate ?? ev?.start_date ?? ev?.date ?? ev?.start);

const getEventUntilDate = (ev) =>
  parseDateSafe(ev?.until ?? ev?.untilDate ?? ev?.repeatUntil ?? ev?.repeat_until ?? ev?.endDate ?? ev?.end_date);

const getRecurringOccurrencesInMonth = (ev, currentYear, currentMonth) => {
  const monthStart = new Date(currentYear, currentMonth, 1);
  monthStart.setHours(0, 0, 0, 0);

  const monthEnd = new Date(currentYear, currentMonth + 1, 0);
  monthEnd.setHours(0, 0, 0, 0);

  const start = getEventStartDate(ev);
  if (!start) return [];

  const until = getEventUntilDate(ev);
  const startDay = toDateOnly(start);
  const untilDay = until ? toDateOnly(until) : null;

  const rrule = parseRRule(ev?.rrule ?? ev?.RRULE ?? ev?.recurrenceRule ?? ev?.recurrence_rule ?? "");

  const isRecursive = Number(ev?.isRecursive) === 1 || ev?.isRecursive === true;

  const freqRaw =
    rrule?.freq ||
    String(
      ev?.frequency ??
      ev?.freq ??
      ev?.repeatType ??
      ev?.repeat ??
      ev?.repeatFrequency ??
      ev?.repeatitionIntervalType ??
      ev?.repetitionIntervalType ??
      ""
    )
      .trim()
      .toUpperCase();

  let freq =
    freqRaw.includes("WEEK") ? "WEEKLY" :
      freqRaw.includes("MONTH") ? "MONTHLY" :
        freqRaw.includes("DAY") ? "DAILY" :
          (rrule?.freq || "");

  if (!freq && isRecursive) freq = "WEEKLY";

  const hasWeekDays =
    !!extractWeekdays(ev)?.length ||
    (rrule?.byday?.length);

  if (!freq && hasWeekDays) freq = "WEEKLY";

  const untilDateOnly = untilDay || (rrule?.until ? toDateOnly(rrule.until) : null);

  const isAllowed = (d) => {
    const dd = toDateOnly(d);
    if (!dd) return false;
    if (dd.getTime() < monthStart.getTime() || dd.getTime() > monthEnd.getTime()) return false;
    if (startDay && dd.getTime() < startDay.getTime()) return false;
    if (untilDateOnly && dd.getTime() > untilDateOnly.getTime()) return false;
    return true;
  };

  if (!freq) {
    return startDay && isAllowed(startDay) ? [startDay] : [];
  }

  if (freq === "WEEKLY") {
    let days = extractWeekdays(ev);

    if (!days && rrule?.byday?.length) days = rrule.byday;

    const fallbackDow = start.getDay();
    const weekDays = (days && days.length ? days : [fallbackDow])
      .map(normalizeWeekday)
      .filter((x) => x != null);

    const out = [];
    for (const dow of weekDays) {
      let cursor = new Date(Math.max(monthStart.getTime(), startDay.getTime()));
      cursor.setHours(0, 0, 0, 0);

      const shift = (dow - cursor.getDay() + 7) % 7;
      cursor.setDate(cursor.getDate() + shift);

      while (cursor.getTime() <= monthEnd.getTime()) {
        if (isAllowed(cursor)) out.push(new Date(cursor.getTime()));
        cursor.setDate(cursor.getDate() + 7);
      }
    }

    const uniq = Array.from(new Map(out.map((d) => [d.toISOString(), d])).values()).sort(
      (a, b) => a.getTime() - b.getTime()
    );
    return uniq;
  }

  if (freq === "MONTHLY") {
    const byMonthDay =
      (Array.isArray(ev?.byMonthDay) ? ev.byMonthDay :
        Array.isArray(ev?.monthDays) ? ev.monthDays :
          Array.isArray(ev?.repeatOnDays) ? ev.repeatOnDays :
            null);

    const doms =
      (byMonthDay && byMonthDay.length
        ? byMonthDay
        : (rrule?.bymonthday?.length ? rrule.bymonthday : [start.getDate()]))
        .map((n) => Number(n))
        .filter((n) => !isNaN(n) && n >= 1 && n <= 31);

    const out = [];
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
    for (const dom of doms) {
      if (dom > daysInMonth) continue;
      const d = new Date(currentYear, currentMonth, dom);
      d.setHours(0, 0, 0, 0);
      if (isAllowed(d)) out.push(d);
    }
    out.sort((a, b) => a.getTime() - b.getTime());
    return out;
  }

  if (freq === "DAILY") {
    const out = [];
    let cursor = new Date(Math.max(monthStart.getTime(), startDay.getTime()));
    cursor.setHours(0, 0, 0, 0);
    while (cursor.getTime() <= monthEnd.getTime()) {
      if (isAllowed(cursor)) out.push(new Date(cursor.getTime()));
      cursor.setDate(cursor.getDate() + 1);
    }
    return out;
  }

  return startDay && isAllowed(startDay) ? [startDay] : [];
};

/* ---------------- Calendar occurrences (DATE cards) ---------------- */

const fmtRangeSmart = (start, end) => {
  if (!start) return "—";
  if (!end || sameDay(start, end)) return formatDateDisplay(start);
  return `${formatDateDisplay(start)} - ${formatDateDisplay(end)}`;
};

const getEventLocationFull = (event) => {
  const address = event?.location ? String(event.location).trim() : "";
  const city = event?.city_Name ? String(event.city_Name).trim() : "";
  const state = event?.state_Name ? String(event.state_Name).trim() : "";
  const country = event?.country_Name ? String(event.country_Name).trim() : "";
  const parts = [address, city, state, country].filter(Boolean);
  return parts.join(", ");
};

const buildOccurrencesByDateForMonth = (calendarEvents, currentYear, currentMonth, cutoffDate) => {
  const monthStart = new Date(currentYear, currentMonth, 1);
  monthStart.setHours(0, 0, 0, 0);
  const monthEnd = new Date(currentYear, currentMonth + 1, 0);
  monthEnd.setHours(0, 0, 0, 0);

  const map = new Map(); // key -> { date, items: [{event, occStart, occEnd}] }

  const add = (dateOnly, payload) => {
    const d = toDateOnly(dateOnly);
    if (!d) return;
    if (d < monthStart || d > monthEnd) return;
    if (cutoffDate && d < cutoffDate) return;

    const k = dateKey(d);
    if (!map.has(k)) map.set(k, { date: d, items: [] });
    map.get(k).items.push(payload);
  };

  (calendarEvents || []).forEach((ev) => {
    // Instances: only the instance day; occEnd = computed endDate (already set)
    if (ev?.__isInstance) {
      const occStart = toDateOnly(ev?.startDate);
      const occEnd = toDateOnly(ev?.endDate) || occStart;
      add(occStart, { event: ev, occStart, occEnd });
      return;
    }

    // Recurring occurrences in this month (each occurrence is same-day)
    const occ = getRecurringOccurrencesInMonth(ev, currentYear, currentMonth) || [];
    if (occ.length) {
      occ.forEach((d) => {
        const dd = toDateOnly(d);
        add(dd, { event: ev, occStart: dd, occEnd: dd });
      });
      return;
    }

    // Normal multi-day event: include in each day of the span
    const start = toDateOnly(ev?.startDate ?? ev?.start_date ?? ev?.date ?? ev?.start);
    const end = toDateOnly(ev?.endDate ?? ev?.end_date ?? ev?.end) || start;
    if (!start) return;

    const spanDays = Math.max(0, Math.min(60, Math.round((end.getTime() - start.getTime()) / (24 * 60 * 60 * 1000))));
    for (let i = 0; i <= spanDays; i++) {
      const day = new Date(start);
      day.setDate(start.getDate() + i);
      day.setHours(0, 0, 0, 0);
      add(day, { event: ev, occStart: start, occEnd: end });
    }
  });

  // Sort items per date by startTime (best-effort)
  for (const entry of map.values()) {
    entry.items.sort((a, b) => {
      const at = (a?.event?.startTime ?? a?.event?.start_time ?? "").toString();
      const bt = (b?.event?.startTime ?? b?.event?.start_time ?? "").toString();
      return at.localeCompare(bt);
    });
  }

  // Return sorted list
  return Array.from(map.entries())
    .map(([k, v]) => ({ key: k, ...v }))
    .sort((a, b) => a.date.getTime() - b.date.getTime());
};

/* ---------------- compact calendar (DOT only; NO event highlight) ---------------- */
/* ---------------- compact calendar (PAST = DOT, FUTURE = HIGHLIGHT) ---------------- */
const CalendarComponent = ({ events = [], currentMonth, currentYear }) => {
  const [selectedDay, setSelectedDay] = useState(null);

  const today0 = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const monthStart = useMemo(() => {
    const d = new Date(currentYear, currentMonth, 1);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentYear, currentMonth]);

  const monthEnd = useMemo(() => {
    const d = new Date(currentYear, currentMonth + 1, 0);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [currentYear, currentMonth]);

  // Build map: dayOfMonth -> { count, hasPast, hasFuture }
  const eventsByDay = useMemo(() => {
    const map = new Map();

    const add = (d) => {
      const dd = toDateOnly(d);
      if (!dd) return;
      if (dd < monthStart || dd > monthEnd) return;

      const dom = dd.getDate();
      const prev = map.get(dom) || { count: 0, hasPast: false, hasFuture: false };

      const isFuture = dd.getTime() > today0.getTime();
      const isPastOrToday = dd.getTime() <= today0.getTime();

      map.set(dom, {
        count: prev.count + 1,
        hasPast: prev.hasPast || isPastOrToday,
        hasFuture: prev.hasFuture || isFuture,
      });
    };

    (events || []).forEach((ev) => {
      // instance: only its day
      if (ev?.__isInstance) {
        add(ev?.startDate);
        return;
      }

      // recurring occurrences in this month
      const occ = getRecurringOccurrencesInMonth(ev, currentYear, currentMonth) || [];
      if (occ.length) {
        occ.forEach((d) => add(d));
        return;
      }

      // normal event (start day only for dot/highlight)
      add(ev?.startDate ?? ev?.start_date ?? ev?.date ?? ev?.start);
    });

    return map;
  }, [events, currentMonth, currentYear, monthStart, monthEnd, today0]);

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();
  const weekdayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const isSameMonthAsToday =
    currentMonth === today0.getMonth() && currentYear === today0.getFullYear();

  return (
    <div className="w-full mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
        <div className="md:col-span-8">
          <div className="grid grid-cols-7 gap-0 mb-1">
            {weekdayNames.map((day) => (
              <div key={day} className="text-center text-xs md:text-sm font-semibold text-gray-600">
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {Array.from({ length: firstDayOfMonth }, (_, i) => (
              <div key={`empty-${i}`} className="h-5 md:h-10" />
            ))}

            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;

              const cellDate = new Date(currentYear, currentMonth, day);
              cellDate.setHours(0, 0, 0, 0);

              const isTodayCell = isSameMonthAsToday && day === today0.getDate();
              const status = eventsByDay.get(day);
              const hasEvent = !!status;
              const hasFutureEvent = !!status?.hasFuture; // future event day => highlight
              const hasPastEvent = !!status?.hasPast; // past/today => dot
              const isSelected = selectedDay === day;

              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedDay(day)}
                  className={cx(
                    "h-5 md:h-10 w-full flex flex-col items-center justify-center text-sm rounded-lg cursor-pointer transition-colors relative border",
                    "border-gray-100",

                    // Priority order: Today → Future → Past → Normal
                    isTodayCell
                      ? "bg-[#D2B995] text-white font-bold border-[#C7A971]"
                      : hasFutureEvent
                        ? "bg-[#FFF5E6] text-[#A67C37] font-semibold border-[#E7D3B0] hover:bg-[#FFF1DD]"
                        : hasPastEvent
                          ? "bg-[#FFF5E6] text-gray-700 border-[#EDE4D6]"
                          : "bg-white text-gray-700 hover:bg-gray-100",

                    isSelected ? "ring-2 ring-[#A67C37]" : ""
                  )}
                  aria-label={`Day ${day}${hasEvent ? " with events" : ""}`}
                >
                  <span>{day}</span>

                  {/* Past event dot */}
                  {hasPastEvent && !hasFutureEvent && (
                    <span className="w-2 h-2 bg-[#A67C37] rounded-full mt-1 opacity-80" />
                  )}

                  {/* Future event dot */}
                  {hasFutureEvent && (
                    <span className="w-2 h-2 bg-[#A67C37] rounded-full mt-1" />
                  )}
                </button>

              );
            })}
          </div>
        </div>

        <div className="md:col-span-4">
          <div className="rounded-lg border-2 border-[#D2B995] bg-[#F5F1E8] px-4 py-4 h-full">
            <div className="mb-3 text-center">
              {/* <div className="text-sm font-semibold text-[#A67C37]">
                <br />
              </div> */}
            </div>

            <div className="flex flex-col items-center md:items-center gap-1">
              <div className="flex items-center gap-2 rounded-full px-1 py-3">
                <span className="inline-block w-8 h-8 rounded bg-[#D2B995] border border-white/50"></span>
                <span className="text-black text-xs">Today's date</span>
              </div>

              {/* <div className="flex items-center gap-2 rounded-full px-1 py-3">
                <span className="inline-block w-8 h-8 rounded bg-white border border-gray-200 relative">
                  <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-[#A67C37]" />
                </span>
                <span className="text-black text-xs">Past events (dot)</span>
              </div> */}

              <div className="flex items-center gap-2 rounded-full ml-2 px-1 py-3">
                <span className="inline-block w-8 h-8 rounded bg-[#FFF5E6] border border-[#E7D3B0]" />
                <span className="text-black text-xs">Day's of event</span>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};


/* ---------------- Date Cards List (Grouped by date) ---------------- */
const MonthEventCardsByDate = ({ calendarEvents, currentMonth, currentYear }) => {
  const todayDateOnly = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const isViewPastMonth =
    currentYear < todayDateOnly.getFullYear() ||
    (currentYear === todayDateOnly.getFullYear() && currentMonth < todayDateOnly.getMonth());

  const isViewCurrentMonth =
    currentYear === todayDateOnly.getFullYear() && currentMonth === todayDateOnly.getMonth();

  const cutoffDate = isViewCurrentMonth ? todayDateOnly : null;

  const dateGroups = useMemo(() => {
    if (isViewPastMonth) return [];
    return buildOccurrencesByDateForMonth(calendarEvents || [], currentYear, currentMonth, cutoffDate);
  }, [calendarEvents, currentYear, currentMonth, cutoffDate, isViewPastMonth]);

  return (
    <div className="border-t border-gray-200 flex-1 min-h-0">
      {/* scroll fills remaining height of calendar panel */}
      <div className="p-3 bg-transparent h-full flex flex-col min-h-0">
        <div id="scroll" className="bg-transparent rounded-md overflow-y-auto flex-1 min-h-0 pr-1">
          {dateGroups.length ? (
            <div className="flex flex-col gap-3">
              {dateGroups.map((g) => {
                const isToday = sameDay(g.date, todayDateOnly);

                return (
                  <div
                    key={g.key}
                    className={cx(
                      "bg-white rounded-lg border shadow-sm overflow-hidden",
                      isToday ? "border-[#A67C37]" : "border-gray-200"
                    )}
                  >
                    {/* Card body with LEFT date badge */}
                    <div className="flex items-center gap-4 px-3 py-2">

                      {/* LEFT: Date badge */}
                      <div className="flex flex-col items-center justify-center w-[56px] h-[56px] rounded-lg bg-[#D2B995] text-white shrink-0">
                        <div className="text-lg font-bold leading-none">
                          {g.date.getDate()}
                        </div>
                        <div className="text-xs uppercase tracking-wide">
                          {g.date.toLocaleString("en-US", { month: "short" })}
                        </div>
                      </div>

                      {/* RIGHT: Events list (unchanged) */}
                      <div className="flex-1 max-h-[100px] overflow-y-auto">
                        <ul className="divide-y divide-gray-100">
                          {g.items.map((it, idx) => {
                            const ev = it.event;

                            const title = ev?.name || ev?.title || "Event";
                            const startTime = ev?.startTime
                              ? String(ev.startTime).trim()
                              : "";
                            const endTime = ev?.endTime
                              ? String(ev.endTime).trim()
                              : "";
                            const timeText =
                              startTime && endTime
                                ? `${startTime} - ${endTime}`
                                : startTime || endTime || "";

                            const loc = getEventLocationFull(ev);
                            const dateText = fmtRangeSmart(
                              it.occStart,
                              it.occEnd
                            );
                            const desc = ev?.description
                              ? String(ev.description)
                              : "";

                            return (
                              <li key={`${g.key}-${idx}`} className="px-1 py-1">
                                <div className="min-w-0">
                                  <div className="font-semibold text-sm text-gray-800 truncate">
                                    {title}
                                  </div>

                                  <div className="mt-1 flex flex-wrap items-center gap-2 justify-between">
                                    {timeText && (
                                      <span className="text-xs text-gray-600">
                                        ⏰ {timeText}
                                      </span>
                                    )}

                                    <span className="text-xs bg-white border-3 text-black px-2 py-[0.1] rounded-full shadow-sm">
                                      <span className="text-xs bg-[white] border-3 text-black px-2 py-[0.1] rounded-full shadow-sm">
                                        📅 {dateText}
                                      </span>


                                    </span>
                                  </div>

                                  {loc && (
                                    <div className="mt-1 text-xs text-gray-600 whitespace-pre-wrap break-words">
                                      📍 {loc}
                                    </div>
                                  )}

                                  {desc && (
                                    <p className="mt-1 text-[11px] text-gray-500 whitespace-pre-wrap">
                                      {desc}
                                    </p>
                                  )}
                                </div>

                              </li>
                            );
                          })}
                        </ul>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6">
              <p className="text-gray-500 text-sm">No remaining events</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ---------------- Monthly Prayer Times (IQAMAH TIME CHANGE) ---------------- */
const MonthlyPrayerTimes = ({ yearlyIqamaTimeDtos = [], iqamahSchedule = [], prayerTimesData }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const fmt = (t) => (t ? String(t).trim().replace(/^0/, "") : "—");

  // ---- Build columns from yearlyIqamaTimeDtos (primary) ----
  let columns = [];

  if (yearlyIqamaTimeDtos.length > 0) {
    // Sort by effectiveDate ascending
    const sorted = [...yearlyIqamaTimeDtos].sort(
      (a, b) => new Date(a.effectiveDate) - new Date(b.effectiveDate)
    );

    // Find the entry whose range contains today
    let startIdx = sorted.findIndex((entry) => {
      const start = new Date(entry.effectiveDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(entry.endDate);
      end.setHours(23, 59, 59, 999);
      return today >= start && today <= end;
    });

    // If no current range found, take the next upcoming one
    if (startIdx < 0) {
      startIdx = sorted.findIndex((entry) => new Date(entry.effectiveDate) > today);
    }

    // Take 3 consecutive entries (or fewer if near end of data)
    const slice = startIdx >= 0 ? sorted.slice(startIdx, startIdx + 3) : sorted.slice(0, 3);

    columns = slice.map((entry) => ({
      key: String(entry.id ?? entry.effectiveDate),
      label: entry.effective_Date_Month_Range || entry.daterange || "—",
      times: {
        Fajr: fmt(entry.fajr_Iqama),
        Dhuhr: fmt(entry.duhr_Iqama),
        Asr: fmt(entry.asr_Iqama),
        Maghrib: (entry.maghrib_Iqama || "").trim().toLowerCase() === "sunset"
          ? "Sunset"
          : fmt(entry.maghrib_Iqama),
        Isha: fmt(entry.isha_Iqama),
      },
    }));
  }

  // ---- Fallback: old iqamahSchedule logic ----
  if (columns.length === 0 && iqamahSchedule.length > 0) {
    const baseYear = today.getFullYear();
    const baseMonthIdx = today.getMonth();
    const dom = today.getDate();
    const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    const addMonths = (d, n) => {
      const dt = new Date(d.getTime()); dt.setDate(1); dt.setMonth(dt.getMonth() + n); return dt;
    };
    const daysInMonthFromOffset = (offset) => {
      const mdate = addMonths(new Date(baseYear, baseMonthIdx, 1), offset);
      return new Date(mdate.getFullYear(), mdate.getMonth() + 1, 0).getDate();
    };
    const canonicalRangesForOffset = (offset) => {
      const dim = daysInMonthFromOffset(offset);
      return ["1-10", "11-20", `21-${dim}`];
    };
    const normalizeRange = (rangeStr, offset) => {
      const dim = daysInMonthFromOffset(offset);
      const m = String(rangeStr || "").match(/^\s*(\d+)\s*-\s*(\d+)\s*$/);
      if (!m) return null;
      const start = Number(m[1]);
      if (start <= 10) return "1-10";
      if (start <= 20) return "11-20";
      return `21-${dim}`;
    };
    const byMonthAndRange = new Map();
    const byRangeFallback = new Map();
    iqamahSchedule.forEach((item) => {
      const mo = Number(item?.monthOffset || 0);
      const canon = normalizeRange(item?.range, mo);
      if (!canon) return;
      const t = item?.times && typeof item.times === "object" ? item.times : item;
      const exactKey = `${mo}:${canon}`;
      if (!byMonthAndRange.has(exactKey)) byMonthAndRange.set(exactKey, t);
      if (!byRangeFallback.has(canon)) byRangeFallback.set(canon, t);
    });
    const currentRangeIdx = dom <= 10 ? 0 : dom <= 20 ? 1 : 2;
    columns = Array.from({ length: 3 }).map((_, i) => {
      const globalIdx = currentRangeIdx + i;
      const monthAdd = Math.floor(globalIdx / 3);
      const idxInMonth = globalIdx % 3;
      const targetMonthDate = addMonths(new Date(baseYear, baseMonthIdx, 1), monthAdd);
      const labelMonth = monthNames[targetMonthDate.getMonth()];
      const targetCanonRanges = canonicalRangesForOffset(monthAdd);
      const targetRange = targetCanonRanges[idxInMonth];
      const exactKey = `${monthAdd}:${targetRange}`;
      const times = byMonthAndRange.get(exactKey) || byRangeFallback.get(targetRange) || byMonthAndRange.get(`0:${targetRange}`) || {};
      return { key: `${monthAdd}-${targetRange}`, label: `${labelMonth} ${targetRange}`, times };
    });
  }

  const prayers = ["Fajr", "Dhuhr", "Asr", "Maghrib", "Isha"];
  const row = (p) => columns.map((c) => c.times?.[p] ?? "—");

  const jummahs = getJummahsFromData(prayerTimesData || {});
  const jummahStart = (j) => {
    const val = j.start || j.startTime || j.jummahStart || j.khutbah || j.khutbahTime || j.iqamah || j.lecture_Time || "";
    return val ? String(val).trim().replace(/^0/, "") : "—";
  };

  return (
    <div className="bg-white">
      {columns.length === 0 ? (
        <div className="p-4 text-center text-gray-500">No IQAMAH TIME CHANGE schedule available.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-full">
            <thead>
              <tr className="bg-[#F5F1E8]">
                <th className="px-3 py-0.1 text-left font-semibold text-[#A67C37] text-base sm:text-lg md:text-xl border-r border-[#E5DBC9] min-w-[80px]">
                  Prayer
                </th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                      className="px-2 py-0.1 text-center font-semibold text-[#A67C37] text-base sm:text-lg md:text-xl border-r border-[#E5DBC9] last:border-r-0 min-w-[90px]"
                  >
                    {c.label}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {prayers.map((p, i) => (
                <tr key={p} className={i % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-3 py-3 font-semibold text-gray-800 text-base sm:text-lg md:text-xl border-r border-gray-200 whitespace-nowrap">
                    {p}
                  </td>
                  {row(p).map((val, idx) => (
                    <td
                      key={`${p}-${idx}`}
                      className="px-2 py-2 text-center border-r border-gray-200 text-[3.4svh] last:border-r-0"
                    >
                      <div className="text-sm font-bold text-[3.4svh] text-[#D2B995]">{val}</div>
                    </td>
                  ))}
                </tr>
              ))}

              {jummahs.map((jummah, jummahIdx) => (
                <tr key={`jummah-row-${jummah.sequence ?? jummahIdx}`} className={jummahIdx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                  <td className="px-3 py-3 font-semibold text-gray-800 text-base sm:text-lg md:text-xl border-r border-gray-200 whitespace-nowrap">
                    {`Jumu'ah ${jummahIdx + 1}`}
                  </td>
                  {columns.map((_, idx) => (
                    <td
                      key={`jummah-${jummah.sequence ?? jummahIdx}-${idx}`}
                      className="px-2 py-2 text-center border-r border-gray-200 text-[3.2svh] last:border-r-0"
                    >
                      <div className="text-sm font-bold text-[#D2B995]">{jummahStart(jummah)}</div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/* ---------------- Services card ---------------- */
const FALLBACK_SERVICE_IMG = "https://placehold.co/600x400?text=Service";
const normalizeService = (s, i = 0) => ({
  id: s?.id ?? s?.serviceId ?? i + 1,
  title: s?.title ?? s?.name ?? "Service",
  description: s?.description ?? s?.desc ?? "",
  image: s?.image ?? s?.imageUrl ?? s?.thumbnail ?? "",
  category: s?.category ?? s?.type ?? "General",
});
const ServicesSection = ({ services = [], setCurrentPage, loading = false }) => {
  const data = Array.isArray(services) ? services.map(normalizeService) : [];
  return (
    <section className="services py-16 bg-gray-50">
      <div className="w-full px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Services & Programs</h2>
        </div>
        {loading && data.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-lg shadow animate-pulse h-72" />
            ))}
          </div>
        )}
        {!loading && data.length === 0 && <div className="text-center text-gray-600">No services to show.</div>}
        {data.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {data.map((service, idx) => (
              <div
                key={`${service.id ?? "svc"}-${idx}`}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="h-48 overflow-hidden">
                  <a data-fancybox="services" href={service.image || FALLBACK_SERVICE_IMG} data-caption={service.title || "Service"} className="h-full w-full block">
                    <img
                      src={service.image || FALLBACK_SERVICE_IMG}
                      alt={service.title || "Service"}
                      className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => (e.currentTarget.src = FALLBACK_SERVICE_IMG)}
                    />
                  </a>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-xl font-bold text-gray-800">{service.title}</h3>
                    <span className="text-xs rounded-full px-2 py-1 bg-gray-100 text-gray-600">
                      {service.category || "General"}
                    </span>
                  </div>
                  {service.description ? (
                    <p className="text-gray-600 mb-4">{service.description}</p>
                  ) : (
                    <p className="text-gray-500 italic mb-4">Details coming soon.</p>
                  )}
                  <button
                    onClick={() => setCurrentPage("services")}
                    className="bg-[#D2B995] text-white px-6 py-2 rounded-lg hover:bg-[#C4A882] transition-colors font-semibold"
                  >
                    Read More
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

/* ---------------- Quran section ---------------- */
const QuranSection = () => (
  <section className="quran-section py-16 bg-[#f7ecd9]">
    <div className="w-full px-4">
      <div className="max-w-4xl mx-auto p-6 bg-[#f7ecd9] rounded-2xl">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2d2a26] mb-4 font-serif">Quran Translation</h1>
          <p className="text-lg text-[#7c6f57] italic">Your daily learning and process of understanding the Holy Quran</p>
        </div>
        <div className="mb-8">
          <div className="text-center mb-4">
            <p className="text-3xl font-arabic text-[#d2b995] font-bold">بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ</p>
          </div>
          <div className="bg-[#cbb38a] rounded-full px-8 py-6 text-center">
            <p className="text-2xl md:text-3xl font-arabic text-[#2d2a26] leading-loose font-bold">
              قُلْ هُوَ اللَّهُ أَحَدٌ ۝ اللَّهُ الصَّمَدُ ۝ لَمْ يَلِدْ وَلَمْ يُولَدْ ۝ وَلَمْ يَكُن لَّهُ كُفُوًا أَحَدٌ ۝
            </p>
          </div>
        </div>
        <div className="mt-8 text-center">
          <p className="text-lg text-gray-600 mb-6 italic">In the name of Allah, the Entirely Merciful, the Especially Merciful.</p>
          <p className="text-lg leading-relaxed text-[#2d2a26] font-serif">
            Say, "He is Allah, [who is] One. Allah, the Eternal Refuge. He neither begets nor is born. Nor is there to Him any equivalent."
          </p>
        </div>
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500 italic">Surah Al-Ikhlas (The Sincerity) - Chapter 112</p>
        </div>
      </div>
    </div>
  </section>
);

/* -------------------- HOME PAGE -------------------- */
export default function HomePage({
  setCurrentPage,
  prayerTimes: prayerTimesProp,
  events = [],
  services = [],
  hijriDate,
  dataLoading,
}) {
  const [eventIndex, setEventIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  const [prayerTimesData, setPrayerTimesData] = useState(prayerTimesProp || null);
  const [iqamahSchedule, setIqamahSchedule] = useState([]);
  const [yearlyIqamaDtos, setYearlyIqamaDtos] = useState([]);

  useEffect(() => setPrayerTimesData(prayerTimesProp || null), [prayerTimesProp]);

  useEffect(() => {
    let alive = true;
    if (prayerTimesProp) return;

    (async () => {
      try {
        const res = await getPrayerTimes(MASJID_ID);
        if (!alive) return;
        setPrayerTimesData(res || {});
        setYearlyIqamaDtos(res?.yearlyIqamaTimeDtos || []);

        const embedded =
          getProp(res, ["iqamahChangeSchedule", "IqamahChangeSchedule"]) ||
          getProp(res, ["iqamahSchedule", "IqamahSchedule"]) ||
          getProp(res, ["IqamaSchedule", "iqamaSchedule"]);

        let mapped = mapRawIqamahSchedule(embedded);
        if (!mapped.length) mapped = buildDefaultIqamahScheduleFromPrayerTimes(res);
        setIqamahSchedule(mapped || []);
      } catch (e) {
        console.warn("getPrayerTimes failed:", e?.message || e);
      }
    })();

    return () => {
      alive = false;
    };
  }, [prayerTimesProp]);

  useEffect(() => {
    if (!prayerTimesProp) return;
    setYearlyIqamaDtos(prayerTimesProp?.yearlyIqamaTimeDtos || []);
    const embedded =
      getProp(prayerTimesProp, ["iqamahChangeSchedule", "IqamahChangeSchedule"]) ||
      getProp(prayerTimesProp, ["iqamahSchedule", "IqamahSchedule"]) ||
      getProp(prayerTimesProp, ["IqamaSchedule", "iqamaSchedule"]);
    let mapped = mapRawIqamahSchedule(embedded);
    if (!mapped.length) mapped = buildDefaultIqamahScheduleFromPrayerTimes(prayerTimesProp);
    setIqamahSchedule(mapped || []);
  }, [prayerTimesProp]);

  const displayEvents = useMemo(
    () => expandEventsWithInstances(events || [], { includeParentRuleCarrier: false }),
    [events]
  );

  const calendarEvents = useMemo(
    () => expandEventsWithInstances(events || [], { includeParentRuleCarrier: true }),
    [events]
  );

  useEffect(() => {
    if (dataLoading) return;
    if (!displayEvents || displayEvents.length < 2) return;
    if (isHovering) return;
    const id = setInterval(() => setEventIndex((prev) => (prev + 1) % displayEvents.length), 3000);
    return () => clearInterval(id);
  }, [displayEvents, isHovering, dataLoading]);

  useEffect(() => {
    if (!displayEvents || displayEvents.length === 0) return;
    if (eventIndex >= displayEvents.length) setEventIndex(0);
  }, [displayEvents, eventIndex]);

  const todayDateOnly = useMemo(() => {
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return t;
  }, []);

  const formattedPrayerTimes = useMemo(
    () => formatPrayerTimesForDisplay(prayerTimesData || {}, { emptyLabel: "—" }),
    [prayerTimesData]
  );

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  return (
    <>
      {/* Banner: Events + Prayer timings */}
      <section className="banner">
        <div className="w-full px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-2">
            {/* Events slider */}
            <div className="lg:col-span-9">
              <div>
                {dataLoading ? (
                  <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-700 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading events...</p>
                    </div>
                  </div>
                ) : displayEvents.length === 0 ? (
                  <div className="flex items-center justify-center h-[500px] bg-gray-50 rounded-lg">
                    <p className="text-gray-600">No events available at the moment.</p>
                  </div>
                ) : (
                  <div className="relative">
                    <button
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white rounded-full shadow p-2 z-10 hover:bg-gray-50"
                      onClick={() =>
                        setEventIndex(eventIndex > 0 ? eventIndex - 1 : displayEvents.length - 1)
                      }
                      aria-label="Previous Event"
                      type="button"
                    >
                      <span className="text-2xl">‹</span>
                    </button>
                    <button
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white rounded-full shadow p-2 z-10 hover:bg-gray-50"
                      onClick={() => setEventIndex((eventIndex + 1) % displayEvents.length)}
                      aria-label="Next Event"
                      type="button"
                    >
                      <span className="text-2xl">›</span>
                    </button>

                    <div
                      className="overflow-hidden rounded-lg"
                      onMouseEnter={() => setIsHovering(true)}
                      onMouseLeave={() => setIsHovering(false)}
                    >
                      <div
                        className="flex transition-transform duration-500 ease-in-out"
                        style={{ transform: `translateX(-${eventIndex * 100}%)` }}
                      >
                        {displayEvents.map((event, index) => {
                          const startD = parseDateSafe(
                            event?.startDate ?? event?.start_date ?? event?.date ?? event?.start
                          );
                          const endD = parseDateSafe(event?.endDate ?? event?.end_date ?? event?.end);

                          const address = event?.location ? String(event.location).trim() : "";
                          const country = event?.country_Name ? String(event.country_Name).trim() : "";
                          const city = event?.city_Name ? String(event.city_Name).trim() : "";
                          const state = event?.state_Name ? String(event.state_Name).trim() : "";

                          const locationParts = [address, city, state, country].filter(Boolean);
                          const locationText = locationParts.join(", ");

                          const startTime = event?.startTime ? String(event.startTime).trim() : "";
                          const endTime = event?.endTime ? String(event.endTime).trim() : "";
                          const timeText = startTime && endTime ? `${startTime} - ${endTime}` : startTime || endTime || "";

                          return (
                            <div key={index} className="flex-shrink-0 w-full">
                              <div className="flex flex-col md:flex-row gap-0 border border-gray-200 rounded-lg bg-white overflow-hidden mt-3 h-[480px]">
                                {/* image */}
                                <div className="md:w-2/3 w-full flex-shrink-0 h-full">
                                  <div className="w-full h-full flex items-center justify-center bg-white">
                                    {event?.image ? (
                                      <img
                                        src={event.image}
                                        alt={event?.name || "Event"}
                                        className="w-full h-full object-contain bg-gray-100"
                                        onError={(e) => {
                                          e.currentTarget.style.display = "none";
                                          const fallback =
                                            e.currentTarget.parentNode.querySelector(".image-fallback");
                                          if (fallback) fallback.style.display = "flex";
                                        }}
                                      />
                                    ) : null}
                                    <div
                                      className="image-fallback bg-gray-100 w-full h-full flex items-center justify-center"
                                      style={{ display: event?.image ? "none" : "flex" }}
                                    >
                                      <span className="text-gray-500">No Image</span>
                                    </div>
                                  </div>
                                </div>

                                {/* details */}
                                <div className="md:w-1/3 w-full flex flex-col justify-center p-6 flex-shrink-0 h-full">
                                  <h3 className="text-xl font-bold text-gray-800 mb-2">
                                    {event?.name || event?.title || "Event"}
                                  </h3>

                                  {(event?.hostName || event?.by) && (
                                    <p className="text-gray-600 mb-3">
                                      <span className="font-semibold">{event?.hostName || event?.by}</span>
                                    </p>
                                  )}

                                  <div className="space-y-2 text-gray-600 mb-3">
                                    {/* Date range (smart: single date if same) */}
                                    {startD ? (
                                      <p className="flex items-center">
                                        <span className="text-green-700 mr-2">📅</span>
                                        <span>{fmtRangeSmart(toDateOnly(startD), toDateOnly(endD) || toDateOnly(startD))}</span>
                                      </p>
                                    ) : null}

                                    {/* Time range */}
                                    {timeText ? (
                                      <p className="flex items-center">
                                        <span className="text-green-700 mr-2">⏰</span>
                                        {timeText}
                                      </p>
                                    ) : null}

                                    {/* Location */}
                                    {locationText ? (
                                      <div className="flex items-start">
                                        <span className="text-green-700 mr-2 mt-[2px]">📍</span>
                                        <span className="whitespace-pre-wrap break-words leading-relaxed">
                                          {locationText}
                                        </span>
                                      </div>
                                    ) : null}
                                  </div>

                                  {/* description */}
                                  {event?.description ? (
                                    <div className="border-top border-gray-100 pt-3 mt-3 overflow-y-auto" style={{ maxHeight: "180px" }}>
                                      <p className="text-gray-600 text-sm whitespace-pre-wrap">{event.description}</p>
                                    </div>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Prayer timings card */}
            <div className="lg:col-span-3 rounded-lg shadow-lg overflow-hidden border-2 border-[#D2B995] mt-3 h-[480px] lg:min-w-[280px]">
              <div className="bg-[#D2B995] px-2 text-center">
                <h3 className="text-1xl font-bold text-white tracking-wide">PRAYER TIMINGS</h3>
              </div>

              <div
                className="text-center py-3 border-b border-[#D2B995] bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${CoolBlue})` }}
              >
                <div className="bg-white/70 inline-block px-1 py-1 rounded-md">
                  <div className="text-sm text-gray-800 font-medium mb-1">
                    {new Date().toLocaleDateString("en-US", {
                      weekday: "long",
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                  <div className="text-xs font-semibold text-[#A67C37]">{hijriDate?.formatted}</div>
                </div>
              </div>

              <div className="flex bg-[#F5F1E8] text-[#A67C37] font-bold text-sm px-4 border-b border-[#D2B995]">
                <div className="w-2/5"></div>
                <div className="w-3/5 flex justify-between">
                  <span>STARTS</span>
                  <span>IQAMAH</span>
                </div>
              </div>

              <div className="bg-white overflow-y-auto" style={{ height: "calc(500px - 180px)" }}>
                <>
                  {formattedPrayerTimes.prayers.map((prayer, idx) => (
                    <div key={idx} className="flex items-center px-4 border-b border-gray-100 mt-1 text-[14px]">
                      <div className="w-2/5 py-[0.2rem] flex items-center gap-2 font-semibold text-gray-800">
                        {prayer.name === "Fajr" && <span>🌙</span>}
                        {prayer.name === "Sunrise" && <span>🌅</span>}
                        {prayer.name === "Dhuhr" && <span>🌞</span>}
                        {prayer.name === "Asr" && <span>🌇</span>}
                        {prayer.name === "Maghrib" && <span>🌆</span>}
                        {prayer.name === "Isha" && <span>🌜</span>}
                        <span>{prayer.name}</span>
                      </div>
                      <div className="w-3/5 flex justify-between text-sm">
                        <span className="text-gray-700">
                          {prayer.name === "Maghrib" && (prayer.starts === "12:undefined AM" || !prayer.starts)
                            ? "Sunset"
                            : prayer.starts}
                        </span>
                        <span className={`font-bold ${prayer.iqama ? "text-[#D2B995]" : "text-gray-400"}`}>
                          {prayer.iqama || (prayer.name === "Maghrib" ? "Sunset" : "")}
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="bg-[#F5F1E8] px-4 font-bold text-center text-[#A67C37] border-t border-[#D2B995] text-[14px]">
                    JUMU&apos;AH
                  </div>
                  <div className="px-4 flex font-semibold border-b border-gray-100 text-[14px]">
                    <div className="w-2/5"></div>
                    <div className="w-3/5 flex justify-between text-sm">
                      <span className="text-[#A67C37]">Start</span>
                      <span className="text-[#A67C37]">Iqamah</span>
                    </div>
                  </div>

                  {(() => {
                    const jummahs = getJummahsFromData(prayerTimesData || {});
                    const fmt = (t) => (t ? String(t).replace(/^0/, "") : "—");
                    return (
                      <>
                        {jummahs.map((jummah, idx) => (
                          <div
                            key={`jummah-card-${jummah.sequence ?? idx}`}
                            className={`px-4 flex items-center text-[14px] py-1 ${idx < jummahs.length - 1 ? "border-b border-gray-100" : ""}`}
                          >
                            <div className="w-2/5 font-semibold text-gray-800">{`${idx + 1}${idx === 0 ? "st" : idx === 1 ? "nd" : idx === 2 ? "rd" : "th"}`}</div>
                            <div className="w-3/5 flex justify-between text-sm">
                              <span className="text-gray-700">{fmt(jummah.lecture_Time || jummah.start)}</span>
                              <span className="font-bold text-[#D2B995]">{fmt(jummah.iqamah)}</span>
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </>
              </div>

              <div className="bg-[#F5F1E8] border-t border-[#D2B995] py-1 px-4">
                <div className="flex justify-between items-center text-center text-xs text-gray-600">
                  <div className="text-center w-full">
                    <span className="text-[#A67C37] text-center">Powered by Serve4good</span>
                  </div>
                </div>
              </div>
            </div>
            {/* /Prayer timings card */}
          </div>
        </div>
      </section>

      {/* Calendar + monthly change */}
      <section className="py-3 bg-white">
        <div className="w-full px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* calendar box (fixed height + internal scroll list) */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-[#D2B995] flex flex-col h-auto lg:h-0 lg:min-h-full">
              <div className="bg-[#D2B995] px-4 py-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-bold text-white tracking-wide">EVENT CALENDAR</h3>
                  <div className="flex items-center gap-4">
                    <div className="text-white font-semibold text-lg">
                      {monthNames[currentMonth]} {currentYear}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          if (currentMonth === 0) {
                            setCurrentMonth(11);
                            setCurrentYear(currentYear - 1);
                          } else setCurrentMonth(currentMonth - 1);
                        }}
                        className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M15 18l-6-6 6-6" />
                        </svg>
                      </button>


                      <button
                        type="button"
                        onClick={() => {
                          if (currentMonth === 11) {
                            setCurrentMonth(0);
                            setCurrentYear(currentYear + 1);
                          } else setCurrentMonth(currentMonth + 1);
                        }}
                        className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center hover:bg-opacity-30"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className="w-5 h-5 text-white"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </button>

                    </div>

                  </div>
                </div>
              </div>

              {/* Calendar (DOT only) */}
              <div className="p-1">
                <CalendarComponent events={calendarEvents} currentMonth={currentMonth} currentYear={currentYear} />
              </div>

              {/* Date cards list (scroll fills remaining height) */}
              <MonthEventCardsByDate calendarEvents={calendarEvents} currentMonth={currentMonth} currentYear={currentYear} />
            </div>

            {/* IQAMAH TIME CHANGE (dynamic-only) */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-[#D2B995]">
              <div className="bg-[#D2B995] px-4 py-3">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white tracking-wide">IQAMAH TIME CHANGE</h3>
                </div>
              </div>
              <div className="p-6">
                <MonthlyPrayerTimes yearlyIqamaTimeDtos={yearlyIqamaDtos} iqamahSchedule={iqamahSchedule} prayerTimesData={prayerTimesData} />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="about py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="bg-gray-200 h-80 rounded-lg shadow-lg overflow-hidden flex items-center justify-center">
              <img src={aboutImg} alt="Mosque" className="h-full w-full object-cover" />
            </div>
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-6">About BICC</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Bramalea Islamic Cultural Centre (BICC) is a registered not-for-profit charitable organization located at 25 Kings Cross Road in Brampton.
              </p>
              <blockquote className="border-l-4 border-[#D2B995] pl-4 italic text-[#D2B995] mb-6">
                Prophet Muhammad (S.A.W.W): "If you wish for Allah to multiply your wealth, then purify it (through zakat)."
              </blockquote>
              <button
                type="button"
                onClick={() => setCurrentPage("about")}
                className="bg-[#D2B995] text-white px-8 py-3 rounded-lg hover:bg-[#C4A882] transition-colors font-semibold"
              >
                Read More
              </button>
            </div>
          </div>
        </div>
      </section>

      <ServicesSection services={services} setCurrentPage={setCurrentPage} loading={dataLoading} />
      <QuranSection />
    </>
  );
}

/* ---------------- helpers for schedule mapping (unchanged behavior) ---------------- */
function normalizeTimesRow(obj = {}) {
  const fajr = getProp(obj, ["Fajr", "fajr", "fajr_iqama", "fajrIqama", "Fajr_Iqama"]);
  const dhuhr = getProp(obj, ["Dhuhr", "dhuhr", "Zuhr", "zuhr", "dhuhr_iqama", "dhuhrIqama", "Zuhr_Iqama"]);
  const asr = getProp(obj, ["Asr", "asr", "asr_iqama", "asrIqama"]);
  const maghrib = getProp(obj, ["Maghrib", "maghrib", "Sunset", "sunset", "maghrib_iqama", "maghribIqama"]);
  const isha = getProp(obj, ["Isha", "isha", "isha_iqama", "ishaIqama"]);
  return {
    Fajr: normalizeTimeString(fajr),
    Dhuhr: normalizeTimeString(dhuhr),
    Asr: normalizeTimeString(asr),
    Maghrib: normalizeTimeString(maghrib),
    Isha: normalizeTimeString(isha),
  };
}

function mapRawIqamahSchedule(raw) {
  if (!raw) return [];
  if (Array.isArray(raw) && raw.length) {
    const arr = raw.map((x) => {
      const mo = Number(x.monthOffset || 0);
      const range =
        x.range ? String(x.range)
          : (x.from && x.to) ? `${x.from}-${x.to}`
            : (x.dayStart && x.dayEnd) ? `${x.dayStart}-${x.dayEnd}`
              : (x.startDay && x.endDay) ? `${x.startDay}-${x.endDay}`
                : "1-10";
      const times = normalizeTimesRow(x.times || x);
      return { range, monthOffset: mo, times };
    });
    const hasAny = arr.some((e) => Object.values(e.times).some(Boolean));
    if (hasAny) return arr;
  }
  if (raw && (raw["0"] || raw["1"])) {
    const out = [];
    for (const moStr of Object.keys(raw)) {
      const mo = Number(moStr);
      const group = raw[moStr] || {};
      for (const key of Object.keys(group)) {
        const val = group[key];
        let range = key;
        if (val && val.from && val.to) range = `${val.from}-${val.to}`;
        if (val && val.dayStart && val.dayEnd) range = `${val.dayStart}-${val.dayEnd}`;
        const times = normalizeTimesRow(val?.times || val);
        out.push({ range: String(range), monthOffset: mo, times });
      }
    }
    const hasAny = out.some((e) => Object.values(e.times).some(Boolean));
    if (hasAny) return out;
  }
  if (typeof raw === "object") {
    const out = [];
    for (const k of Object.keys(raw)) {
      const m = k.match(/^(\d+)_(.+)$/);
      if (m) {
        out.push({
          monthOffset: Number(m[1]),
          range: m[2],
          times: normalizeTimesRow(raw[k]),
        });
      }
    }
    const hasAny = out.some((e) => Object.values(e.times).some(Boolean));
    if (hasAny) return out;
  }
  return [];
}

function buildDefaultIqamahScheduleFromPrayerTimes(prayerTimesData, date = new Date()) {
  const iq = prayerTimesData?.iqamatimes || prayerTimesData?.iqamahTimes || {};
  const base = normalizeTimesRow({
    Fajr: iq.fajr ?? iq.Fajr,
    Dhuhr: iq.dhuhr ?? iq.Dhuhr ?? iq.zuhr ?? iq.Zuhr,
    Asr: iq.asr ?? iq.Asr,
    Maghrib: iq.maghrib ?? iq.Maghrib ?? "Sunset",
    Isha: iq.isha ?? iq.Isha,
  });

  const hasAny = Object.values(base).some(Boolean);
  if (!hasAny) return [];

  const daysInMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const lastRange = `21-${daysInMonth}`;

  return [
    { monthOffset: 0, range: "1-10", times: base },
    { monthOffset: 0, range: "11-20", times: base },
    { monthOffset: 0, range: lastRange, times: base },
  ];
}
